<template>
  <Transition name="slide">
    <div
      v-if="showPw || showEmail || shouldDisplayPaymentFailure"
      :class="['ls-notification-banner', { danger: isWarning }]"
    >
      <Transition name="slide">
        <div
          v-if="showPw && !shouldDisplayPaymentFailure"
          :class="['banner container', { 'border-bottom': showPw && showEmail }]"
        >
          <div class="banner-text-block" test-id="password">
            <span class="has-text-weight-bold banner-text"
              >Uh-oh! It looks like you don't have a password set.
            </span>
            <span class="banner-text">
              Please set your password to ensure you are able to log in as needed.
            </span>
          </div>
          <div class="button-group">
            <LsButton
              test-id="pw"
              text="set password"
              bg-color="primary"
              track-id="ls_notification_banner-set_password"
              @click="openPassword"
            />
          </div>
        </div>
      </Transition>
      <Transition name="slide">
        <div v-if="showEmail && !shouldDisplayPaymentFailure" class="banner container">
          <div class="banner-text-block" test-id="email">
            <span class="has-text-weight-bold banner-text">
              Emails aren't getting through to {{ auth.user.email }}.
            </span>
            <span class="banner-text">Please update or confirm your email address. </span>
          </div>
          <div class="button-group">
            <LsButton
              text="go to profile"
              bg-color="primary"
              track-id="ls_notification-banner_go_to_profile"
              test-id="profile"
              @click="goToProfile"
            />
          </div>
        </div>
      </Transition>
      <Transition name="slide">
        <div v-if="shouldDisplayPaymentFailure" class="banner container">
          <div class="banner-text-block" test-id="payment">
            <span class="has-text-weight-bold banner-text">
              Uh-oh! The charge for your last service didn't go through.
            </span>
            <br />
            <span class="banner-text">
              Please update your payment method so your upcoming service is not disrupted.
            </span>
          </div>
          <div class="button-group">
            <LsButton
              text="update payment"
              bg-color="danger"
              track-id="ls_notification-banner_update_payment"
              test-id="updatePayment"
              @click.native="goToUpdatePayment"
            />
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script>
import { LsButton } from '@/components/common';
import { mapState } from 'vuex';
import { PasswordModal } from '@/modals';
import { lsModalService, lsStorageService, lsStorageServiceConstants } from 'ls-vue-common';
import moment from 'moment';
import { PROPERTY_DEACTIVATED_REASONS, PROPERTY_STATUS } from '@/constants';

export default {
  name: 'LsNotificationBanner',
  components: {
    LsButton,
  },
  data() {
    return {
      showPw: false,
      showEmail: false,
    };
  },
  computed: {
    ...mapState(['auth', 'properties']),
    isWarning() {
      const warningNotifications = [this.shouldDisplayPaymentFailure];
      return warningNotifications.some((notification) => notification);
    },
    hasPropertiesWithPaymentFailure() {
      const { propertiesById } = this.properties;

      return Object.values(propertiesById).some(
        ({ status, can_self_reactivate_reason }) =>
          status === PROPERTY_STATUS.CANCELLED &&
          can_self_reactivate_reason === PROPERTY_DEACTIVATED_REASONS.PAYMENT_FAILURE
      );
    },
    shouldDisplayPaymentFailure() {
      const { customer } = this.auth.user;

      return (
        customer.has_payment_failure &&
        !this.hasPropertiesWithPaymentFailure &&
        this.$route.name !== 'profileUpdatePayment'
      );
    },
  },
  created() {
    const pwTime = lsStorageService.get(lsStorageServiceConstants.NOTIFICATION_PASSWORD);
    const emailTime = lsStorageService.get(lsStorageServiceConstants.NOTIFICATION_EMAIL);
    const { user } = this.auth;

    if (!user.has_password) {
      this.showPw = pwTime ? this.isOverDay({ time: pwTime }) : true;
    }
    if (user.email_bounced) {
      this.showEmail = emailTime ? this.isOverDay({ time: emailTime }) : true;
    }
  },

  methods: {
    openPassword() {
      lsModalService.open({
        parent: this,
        component: PasswordModal,
        props: { type: 'set' },
      });
      this.closePwBanner();
    },
    goToProfile() {
      this.$router.push({ name: 'profile' });
      this.closeEmailBanner();
    },
    goToUpdatePayment() {
      this.$router.push({ name: 'profileUpdatePayment' });
    },
    closePwBanner() {
      lsStorageService.set(lsStorageServiceConstants.NOTIFICATION_PASSWORD, moment().toDate());
      this.showPw = false;
    },
    closeEmailBanner() {
      lsStorageService.set(lsStorageServiceConstants.NOTIFICATION_EMAIL, moment().toDate());
      this.showEmail = false;
    },
    isOverDay({ time }) {
      return moment().diff(time, 'days') >= 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-in-out;
}
.slide-enter,
.slide-leave-to {
  transform: translateY(-64px);
}
.ls-notification-banner {
  z-index: $z-index-notification-banner;
  position: fixed;
  top: $navbar-height;
  left: 0px;
  right: 0px;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: $primary-light;
  .border-bottom {
    border-bottom: 1px solid $primary;
  }
  .button-group {
    display: flex;
    align-items: center;
  }
  .close {
    cursor: pointer;
    padding-left: 20px;
    &:hover {
      opacity: 0.3;
    }
  }
  .banner {
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0;
    width: 100%;
    .banner-text {
      font-size: $size-6;
    }
  }
}

@media (max-width: $mobile-max-width) {
  .ls-notification-banner {
    .banner {
      height: auto;
      flex-direction: column;
      padding: 20px 0;
    }
    .button-group {
      width: 100%;
    }
    .banner-text-block {
      padding-bottom: 10px;
    }
  }
}
.themed-app {
  .ls-notification-banner {
    background-color: $theme-color-primary-light;
    .border-bottom {
      border-bottom: 1px solid $theme-color-primary;
    }
    .banner {
      color: $theme-color-primary;
      .banner-text {
        font-size: var(--theme-txt-body-sm);
      }
    }
    &.danger {
      background-color: $theme-color-danger-light;

      .banner {
        color: $theme-color-danger;
      }
    }
  }
}
</style>
